import { motion } from "framer-motion";
import { ExternalLink, Github, Sparkles, Database, Globe, Cpu } from "lucide-react";

const projects = [
  {
    title: "RAG Portfolio",
    description:
      "This very portfolio - an AI-powered interactive experience using Retrieval-Augmented Generation to answer questions about my work.",
    tags: ["React", "FastAPI", "RAG", "LLM"],
    icon: Sparkles,
    color: "#00d4aa",
  },
  {
    title: "E-Commerce Platform",
    description:
      "A full-stack e-commerce solution with real-time inventory, payment processing, and admin dashboard.",
    tags: ["Next.js", "Node.js", "PostgreSQL", "Stripe"],
    icon: Globe,
    color: "#ffd93d",
  },
  {
    title: "Law Pal",
    description:
      "Law Pal is a multi-agent legal document analysis platform that solves the context window limitations of LLMs by partitioning legal documents and using a distributed agent architecture. Reader Agents analyze semantic chunks in parallel to extract nuanced legal context; Judge Models implement a three-judge consensus mechanism (Legal Accuracy, Completeness, and Reliability) to validate answers and eliminate hallucinations.",
    tags: ["Python", "FastAPI", "React", "Qdrant", "Redis", "Groq"],
    icon: Database,
    color: "#6ee7c2",
  },
];

function ProjectCard({ project, index }) {
  const Icon = project.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="group relative bg-[#16161f] border border-[#252530] rounded-2xl overflow-hidden hover:border-[#00d4aa]/40 transition-all duration-300"
    >
      {/* Glow effect on hover */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{
          background: `radial-gradient(600px circle at var(--mouse-x, 50%) var(--mouse-y, 50%), ${project.color}10, transparent 40%)`,
        }}
      />

      <div className="relative p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${project.color}20` }}
          >
            <Icon className="w-6 h-6" style={{ color: project.color }} />
          </div>
          <div className="flex gap-2">
            <button className="p-2 text-[#606070] hover:text-[#f0f0f5] hover:bg-[#252530] rounded-lg transition-colors">
              <Github className="w-5 h-5" />
            </button>
            <button className="p-2 text-[#606070] hover:text-[#f0f0f5] hover:bg-[#252530] rounded-lg transition-colors">
              <ExternalLink className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <h3
          className="text-xl font-bold text-[#f0f0f5] mb-2"
          style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
        >
          {project.title}
        </h3>
        <p className="text-[#a0a0b0] mb-4 leading-relaxed">
          {project.description}
        </p>

        {/* Tags */}
        <div className="flex flex-wrap gap-2">
          {project.tags.map((tag) => (
            <span
              key={tag}
              className="px-3 py-1 text-xs font-medium rounded-full bg-[#252530] text-[#a0a0b0]"
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Featured badge */}
        {project.featured && (
          <div className="absolute top-4 right-4">
            <span
              className="px-3 py-1 text-xs font-semibold rounded-full"
              style={{
                backgroundColor: `${project.color}20`,
                color: project.color,
              }}
            >
              Featured
            </span>
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default function WorkSection() {
  return (
    <section id="work" className="py-24 px-4 sm:px-6 lg:px-8 relative z-10 bg-[#0c0c12]">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-5xl font-bold gradient-gold mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            My Work
          </h2>
          <p className="text-[#a0a0b0] text-lg max-w-2xl mx-auto">
            A selection of projects I've built and contributed to
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          {projects.map((project, index) => (
            <ProjectCard key={project.title} project={project} index={index} />
          ))}
        </div>

        {/* View all button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-12"
        >
          <a 
            href="https://github.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 border border-[#00d4aa]/30 text-[#00d4aa] rounded-xl hover:bg-[#00d4aa]/10 transition-colors"
          >
            <Github className="w-5 h-5" />
            View all on GitHub
          </a>
        </motion.div>
      </div>
    </section>
  );
}
